import { deviceRealtimeListeners } from '@/entities/device';
import { facilityRealtimeListeners } from '@/entities/facility';
import { inboxRealtimeListeners } from '@/entities/inbox';
import { rmaRealtimeListeners } from '@/entities/rma';
import { studyRealtimeListeners } from '@/entities/study';
import { userRealtimeListeners } from '@/entities/user';
import type { SocketEventListener } from '@/shared/api';
import { SocketIo } from '@/shared/lib/socketIo';
import { sidebarRealtimeListeners } from '@/widgets/sidebar';
import type { Socket } from 'socket.io-client';
import roomPresenceRealtimeListeners, { resetRoomPresenceQueue } from './roomPresenceListeners';

const relocatedListeners: SocketEventListener[] = [
  ...studyRealtimeListeners,
  ...deviceRealtimeListeners,
  ...inboxRealtimeListeners,
  ...facilityRealtimeListeners,
  ...userRealtimeListeners,
  ...rmaRealtimeListeners,
  ...sidebarRealtimeListeners,
  ...roomPresenceRealtimeListeners,
];

// Holds the socket the handlers were bound to, not just a flag: disconnectSocket drops the
// instance and connectToServer builds a new one, so detaching has to target the socket that
// actually carries the handlers rather than whichever one getSocket() returns at detach time.
let attachedSocket: Socket | null = null;

/**
 * Unsubscribes the relocated socket listeners from the socket they were bound to. Removes each event handler through the remembered socket, drops that reference and clears the buffered room-presence events.
 *
 * @returns Nothing.
 */
export const detachRelocatedListeners = () => {
  relocatedListeners.forEach(({ event, handler }) => attachedSocket?.off(event, handler));
  attachedSocket = null;
  resetRoomPresenceQueue();
};

/**
 * Binds the relocated per-domain socket handlers to the live socket. Returns early when no socket exists, first detaches any previous bindings and their queued presence events, then registers each handler and remembers the socket.
 *
 * @returns Nothing.
 */
export const attachRelocatedListeners = () => {
  const socket = SocketIo.getSocket();
  if (!socket) return;
  detachRelocatedListeners();
  relocatedListeners.forEach(({ event, handler }) => socket.on(event, handler));
  attachedSocket = socket;
};
